import React from "react";
import { Edit, Trash2, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const emptyForm = { name: "", role: "", company: "", quote: "", rating: 5, avatar: "", active: true };

export default function AdminTestimonials() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => setItems((await api.get("/admin/testimonials")).data);
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editingId) await api.put(`/admin/testimonials/${editingId}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };

  return (
    <div data-testid="admin-testimonials">
      <Header title="Testimonials" onAdd={() => { setForm(emptyForm); setEditingId(null); setShow(true); }} addLabel="New Testimonial" />
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="card-luxury p-6">
            <Quote className="w-5 h-5 text-[#D4AF37]" />
            <p className="mt-3 text-slate-200 text-sm leading-relaxed">{t.quote}</p>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {t.avatar && <img src={t.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />}
                <div>
                  <div className="text-white text-sm">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.role}{t.company ? ` · ${t.company}` : ""}</div>
                </div>
              </div>
              <div className="flex">
                <IconBtn onClick={() => { setForm({ ...t }); setEditingId(t.id); setShow(true); }}><Edit className="w-3.5 h-3.5" /></IconBtn>
                <IconBtn onClick={() => remove(t.id)} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 text-sm text-center py-10 col-span-2">No testimonials yet.</div>}
      </div>

      {show && (
        <Modal title={editingId ? "Edit testimonial" : "New testimonial"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
              <Field label="Role"><Input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} /></Field>
              <Field label="Company"><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
              <Field label="Rating (1-5)"><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></Field>
            </div>
            <Field label="Avatar URL"><Input value={form.avatar || ""} onChange={(e) => setForm({ ...form, avatar: e.target.value })} /></Field>
            <Field label="Quote"><Textarea rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} required /></Field>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-slate-300">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
